import { http } from './request'
import { TRAVEL_RISK_TAG } from '@/utils/travelRiskLevel'
import {
  formatGeonameidListAsAreaPaths,
  parseGeonameidList,
  type GlobalAreaTreeNode,
} from './globalCountryTree'
import { globalCountryTreeData } from './globalCountryCache'
import { stripHtmlLite } from '@/utils/stripHtml'

/** 预警等级，与国别管理一致：红暂勿前往 / 橙谨慎前往 / 黄密切关注 / 蓝无提醒 */
export type WarningAlertLevel = 'extreme' | 'high' | 'medium' | 'low'

export type WarningReleaseStatusFilter = 'all' | 'draft' | 'published'

/** 内容分类：NEWS 安全要闻，RISK 预警信息 */
export type WarningSourceKind = 'NEWS' | 'RISK'

export const SOURCE_KIND_OPTIONS = [
  { value: 'NEWS', label: '安全要闻' },
  { value: 'RISK', label: '发布预警信息' },
] as const

export function sourceKindLabel(kind?: string | null): string {
  const value = String(kind ?? '')
    .trim()
    .toUpperCase()
  if (value === 'NEWS') return '安全要闻'
  if (value === 'RISK') return '发布预警信息'
  return ''
}

/** 专区同步行：以 sourceUuid 为准，不用 sourceKind 判断 */
export function isZoneSyncedWarning(sourceUuid?: string | null): boolean {
  return Boolean(String(sourceUuid ?? '').trim())
}

/** 信息发布（详情抽屉使用）的简化记录类型 */
export type InformationRecord = {
  id: string
  title: string
  /** 后端状态枚举（如 DRAFT/PUBLISHED/EXPIRED） */
  status: string
  createUserName?: string
  publishTime?: string | null
  viewCount?: number
  /** 富文本 HTML */
  content: string
}

export const informationStatusLabel: Record<string, string> = {
  DRAFT: '草稿',
  PUBLISHED: '已发布',
  EXPIRED: '已过期',
}

/** GET /biz/overseas/warning/detail/{id} */
export async function fetchInformationManageDetail(id: string): Promise<InformationRecord | null> {
  const sid = id?.trim()
  if (!sid) return null
  const raw = await http.get<unknown>(`/biz/overseas/warning/detail/${encodeURIComponent(sid)}`, {
    skipErrorMessage: true,
  })
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = o[k]
      if (v != null && String(v).trim() !== '') return v
    }
    return undefined
  }
  const rid = String(pick('id') ?? '').trim()
  if (!rid) return null
  const title = String(pick('title') ?? '').trim()
  const status = String(pick('status') ?? '').trim()
  const content = String(pick('content') ?? '').trim()
  const viewCountRaw = pick('viewCount', 'view_count')
  const viewCountNum = viewCountRaw == null ? undefined : Number(viewCountRaw)
  return {
    id: rid,
    title,
    status,
    createUserName:
      String(pick('createUserName', 'creatorName', 'create_user_name') ?? '').trim() || undefined,
    publishTime: ((): string | null => {
      const v = pick('publishTime', 'publish_time')
      const s = v == null ? '' : String(v).trim()
      return s ? s : null
    })(),
    viewCount: Number.isFinite(viewCountNum) ? viewCountNum : undefined,
    content,
  }
}

export interface WarningReleaseListQuery {
  /** 发布状态 */
  status?: WarningReleaseStatusFilter
  /** 标题模糊 */
  title?: string
  /** 国家名称/代码模糊 */
  country?: string
  /**
   * LOCAL 仅平台自建（无 sourceUuid）。
   * NEWS / RISK 按内容分类筛，含专区同步；与 sourceKind 同时传时为且关系。
   */
  dataSource?: 'LOCAL' | 'NEWS' | 'RISK'
  /** 内容分类。管理端列表按 Tab 传递 */
  sourceKind?: WarningSourceKind
  /** 风险类型 01 到 09 */
  sourceRiskType?: string
  /** 发布时间开始（yyyy-MM-dd 或 yyyy-MM-dd HH:mm:ss） */
  startDate?: string
  /** 发布时间结束（yyyy-MM-dd 或 yyyy-MM-dd HH:mm:ss） */
  endDate?: string
  /** 页码，从 1 开始 */
  page?: number
  pageSize?: number
}

/** 平台手工预警：原创 / 转载 */
export type WarningContentOrigin = 'original' | 'reprint'

export interface WarningReleaseRecord {
  id: string
  title: string
  /** @deprecated 兼容旧 UI，请使用 targetCountryCodes / targetCountryNames */
  countryCode: string
  /** @deprecated 兼容旧 UI，请使用 targetCountryNames */
  countryName: string
  /** 发布国家 ISO2 编码列表（逗号存储解析后） */
  targetCountryCodes: string[]
  /** 发布国家中文名列表 */
  targetCountryNames: string[]
  /** 预警国家地区 geonameid 列表 */
  targetGeonameids: string[]
  /** 预警国家地区展示文案 */
  regionDisplay: string
  /** 预警等级。安全要闻可不填 */
  level?: WarningAlertLevel
  /** 创建人（优先姓名，没有则为ID） */
  creatorName?: string
  /** 创建日期（YYYY-MM-DD） */
  createAt?: string
  /** 发布时间 */
  publishTime?: string
  /** ISO 8601 */
  validFrom: string
  validTo: string
  detailHtml: string
  /** 列表摘要（纯文本，由详情生成或后台返回） */
  summary: string
  status: 'draft' | 'published'
  updatedAt: string
  sourceRiskTypes: string[]
  sourceOnTap: '0' | '1'
  /** 数据来源展示：平台 / 专区，由 sourceUuid 决定 */
  dataSource: string
  /** NEWS 安全要闻，RISK 预警信息 */
  sourceKind?: WarningSourceKind
  /** 平台手工：原创 / 转载 */
  contentOrigin?: WarningContentOrigin
  /** 外部数据 uuid。有值即为专区同步 */
  sourceUuid?: string
  /** 外部附件原文 */
  sourceFileUrl?: string
  /** 是否涉华：1 是，0 否 */
  sourceInvoleUs?: string
  /** 专区文章来源 articleSource */
  sourceArticleSource?: string
}

export interface WarningReleasePayload {
  /** 内容分类，保存必填 */
  sourceKind: WarningSourceKind
  title: string
  /** 预警国家地区 geonameid 列表。预警信息与安全要闻均必填 */
  targetGeonameids: string[]
  validFrom: string
  validTo: string
  detailHtml: string
  /** 风险类型编码，01 到 09，可多选 */
  sourceRiskTypes?: string[]
  /** 是否置顶：0 否，1 是 */
  sourceOnTap?: '0' | '1'
  /** 内容来源。平台手工保存必填 */
  contentOrigin: WarningContentOrigin
  /** 转载时的文章来源 */
  sourceArticleSource?: string
}

function contentOriginToApi(origin: WarningContentOrigin): 'ORIGINAL' | 'REPRINT' {
  return origin === 'reprint' ? 'REPRINT' : 'ORIGINAL'
}

function contentOriginFromApi(raw: unknown, articleSource?: string): WarningContentOrigin {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (value === 'REPRINT') return 'reprint'
  if (value === 'ORIGINAL') return 'original'
  return String(articleSource ?? '').trim() ? 'reprint' : 'original'
}

function sourceKindFromApi(raw: unknown): WarningSourceKind | undefined {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (value === 'NEWS' || value === 'RISK') return value
  return undefined
}

function buildWarningPublishDto(payload: WarningReleasePayload, extra?: { id?: string }) {
  const reprint = payload.contentOrigin === 'reprint'
  const regions = payload.targetGeonameids.map((id) => id.trim()).filter(Boolean)
  const riskTypes = (payload.sourceRiskTypes ?? []).map((code) => code.trim()).filter(Boolean)
  return {
    ...extra,
    sourceKind: payload.sourceKind,
    title: payload.title,
    content: payload.detailHtml,
    targetRegions: regions.length ? regions.join(',') : undefined,
    validUntil: payload.validTo?.trim() ? toBackendLocalDateTime(payload.validTo) : undefined,
    status: 'DRAFT' as const,
    sourceRiskType: riskTypes.length ? riskTypes.join(',') : undefined,
    sourceOnTap: payload.sourceOnTap === '1' ? '1' : '0',
    contentOrigin: contentOriginToApi(payload.contentOrigin),
    sourceArticleSource: reprint ? payload.sourceArticleSource?.trim() || undefined : undefined,
  }
}

/** 专区风险类型：01–09，列表和表单共用文案 */
export const SOURCE_RISK_TYPE_OPTIONS = [
  { value: '01', label: '政局稳定' },
  { value: '02', label: '恐怖袭击' },
  { value: '03', label: '社会治安' },
  { value: '04', label: '舆情民意' },
  { value: '05', label: '经济金融' },
  { value: '06', label: '法律法规' },
  { value: '07', label: '灾害疫病' },
  { value: '08', label: '地缘政治' },
  { value: '09', label: '建设运营' },
] as const

export function sourceRiskTypeLabel(code: string): string {
  return SOURCE_RISK_TYPE_OPTIONS.find((item) => item.value === code)?.label ?? code
}

/** 列表/详情：是否转载（有文章来源即转载，与平台手工表单逻辑一致） */
export function isWarningReprint(
  record: Pick<WarningReleaseRecord, 'sourceArticleSource'>,
): boolean {
  return Boolean(String(record.sourceArticleSource ?? '').trim())
}

export function warningContentOriginLabel(
  record: Pick<WarningReleaseRecord, 'sourceArticleSource' | 'contentOrigin'>,
): '原创' | '转载' {
  if (record.contentOrigin === 'reprint') return '转载'
  if (record.contentOrigin === 'original') return '原创'
  return isWarningReprint(record) ? '转载' : '原创'
}

/** 平台 / 专区。有 sourceUuid 为专区同步，否则为平台自建。 */
export function warningDataSourceLabel(sourceUuid?: string | null): string {
  return isZoneSyncedWarning(sourceUuid) ? '专区' : '平台'
}

export interface WarningReleaseListResult {
  list: WarningReleaseRecord[]
  total: number
}

type WarningApiRiskLevel = 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW'
type WarningApiStatus = 'DRAFT' | 'PUBLISHED' | 'EXPIRED' | string

interface WarningApiRecord {
  id?: string
  title?: string
  content?: string
  riskLevel?: WarningApiRiskLevel | string
  targetCountries?: string
  targetRegions?: string
  targetCountryCodes?: string
  targetCountryNames?: string
  publishTime?: string | null
  validUntil?: string | null
  status?: WarningApiStatus
  updateTime?: string
  createTime?: string
  createUser?: string
  createUserName?: string
  sourceRiskType?: string
  sourceOnTap?: string
  sourceOrigin?: string
  sourceKind?: string
  contentOrigin?: string
  sourceUuid?: string
  sourceFileUrl?: string
  sourceInvoleUs?: string
  sourceArticleSource?: string
}

interface MybatisPage<T> {
  records?: T[]
  total?: number
  current?: number
  size?: number
}

/** 国家选项（前端选择框；后台联调后可改为接口） */
export const WARNING_RELEASE_COUNTRY_OPTIONS = [
  { value: 'MM', label: '缅甸' },
  { value: 'TH', label: '泰国' },
  { value: 'FR', label: '法国' },
  { value: 'GB', label: '英国' },
  { value: 'US', label: '美国' },
  { value: 'JP', label: '日本' },
  { value: 'EG', label: '埃及' },
  { value: 'LA', label: '老挝' },
  { value: 'VN', label: '越南' },
  { value: 'KH', label: '柬埔寨' },
] as const

export const warningAlertLevelLabel: Record<WarningAlertLevel, string> = TRAVEL_RISK_TAG

export const warningAlertPriorityLabel: Record<WarningAlertLevel, string> = warningAlertLevelLabel

function buildSummary(html: string, maxLen = 120): string {
  const t = stripHtmlLite(html)
  if (t.length <= maxLen) return t
  return `${t.slice(0, maxLen)}…`
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** 后端 LocalDateTime 兼容：yyyy-MM-dd HH:mm:ss */
function toBackendLocalDateTime(inputIso: string): string {
  const d = new Date(inputIso)
  if (Number.isNaN(d.getTime())) return inputIso
  const y = d.getFullYear()
  const m = pad2(d.getMonth() + 1)
  const day = pad2(d.getDate())
  const hh = pad2(d.getHours())
  const mm = pad2(d.getMinutes())
  const ss = pad2(d.getSeconds())
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
}

function riskLevelFromApi(v: unknown): WarningAlertLevel | undefined {
  const s = String(v ?? '')
    .trim()
    .toUpperCase()
  if (s === 'EXTREME') return 'extreme'
  if (s === 'HIGH') return 'high'
  if (s === 'MEDIUM') return 'medium'
  if (s === 'LOW') return 'low'
  return undefined
}

function statusFromApi(v: unknown): 'draft' | 'published' {
  const s = String(v ?? '').toUpperCase()
  if (s === 'PUBLISHED') return 'published'
  return 'draft'
}

/** 将接口 targetCountries（逗号/顿号等分隔）规范为英文逗号存储，供提交与列表回显 */
function normalizeTargetCountriesStorage(raw: string | undefined): string {
  return String(raw ?? '')
    .split(/[,，、]/)
    .map((x) => x.trim())
    .filter(Boolean)
    .join(',')
}

/** 列表/详情展示：顿号分隔的地区路径 */
export function formatWarningTargetRegionsDisplay(
  geonameids: string[] | undefined | null,
  treeNodes: GlobalAreaTreeNode[] = globalCountryTreeData.value,
): string {
  const ids = Array.isArray(geonameids) ? geonameids : []
  if (!ids.length) return ''
  const firstId = ids[0]
  if (ids.length === 1 && firstId?.trim().toUpperCase() === 'ALL') return '全部国家'
  return formatGeonameidListAsAreaPaths(treeNodes, ids)
}

function parseCommaList(raw: string | undefined | null): string[] {
  if (raw == null || String(raw).trim() === '') return []
  return String(raw)
    .split(/[,，、]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/** 列表/详情展示：顿号分隔的国家中文名 */
export function formatWarningTargetCountryNamesDisplay(
  names: string[] | undefined | null,
  fallbackStored?: string | undefined | null,
): string {
  const list = Array.isArray(names) ? names.filter(Boolean) : []
  if (list.length) return list.join('、')
  return formatWarningTargetCountriesDisplay(fallbackStored)
}

/** @deprecated 历史 targetCountries 展示 */
export function formatWarningTargetCountriesDisplay(stored: string | undefined | null): string {
  if (stored == null || String(stored).trim() === '') return ''
  return String(stored)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .join('、')
}

function isoOrFallback(input: unknown, fallbackIso: string): string {
  const s = String(input ?? '').trim()
  if (!s) return fallbackIso
  // 兼容 "2025-03-24 12:00:00" / ISO
  if (s.includes('T')) return s
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.replace(' ', 'T')
  return fallbackIso
}

function formatUpdatedAt(rec: WarningApiRecord): string {
  const s = String(rec.updateTime ?? rec.publishTime ?? rec.createTime ?? '').trim()
  if (!s) return ''
  return s.includes('T') ? s.slice(0, 10) : s.slice(0, 10)
}

function mapWarningApiToRecord(
  rec: WarningApiRecord,
  opts?: { includeContent?: boolean },
): WarningReleaseRecord | null {
  const id = String(rec.id ?? '').trim()
  if (!id) return null
  const targetGeonameids = parseGeonameidList(rec.targetRegions)
  const includeContent = opts?.includeContent === true
  let regionDisplay = formatWarningTargetCountryNamesDisplay(
    parseCommaList(rec.targetCountryNames),
    rec.targetCountries,
  )
  // 列表不扫国家树（数据量大时会卡）；编辑/详情再按 geonameid 解析路径
  if (!regionDisplay && targetGeonameids.length && includeContent) {
    regionDisplay = formatWarningTargetRegionsDisplay(targetGeonameids)
  }
  const targetCountryCodes = parseCommaList(rec.targetCountryCodes)
  const targetCountryNames = parseCommaList(rec.targetCountryNames)
  const countryDisplay = formatWarningTargetCountryNamesDisplay(
    targetCountryNames,
    rec.targetCountries,
  )
  const validAtRaw = String(rec.validUntil ?? '').trim()
  const validAt = validAtRaw ? isoOrFallback(rec.validUntil, '') : ''
  const detailHtml = includeContent ? String(rec.content ?? '').trim() || '<p></p>' : ''
  const creatorName =
    String(rec.createUserName ?? '').trim() || String(rec.createUser ?? '').trim() || undefined
  const createAtRaw = String(rec.createTime ?? '').trim()
  const createAt = createAtRaw ? createAtRaw.replace('T', ' ').slice(0, 19) : undefined
  return {
    id,
    title: String(rec.title ?? '').trim(),
    countryCode: targetCountryCodes.join(','),
    countryName: countryDisplay,
    targetCountryCodes,
    targetCountryNames,
    targetGeonameids,
    regionDisplay,
    level: riskLevelFromApi(rec.riskLevel),
    creatorName,
    createAt,
    publishTime: rec.publishTime
      ? String(rec.publishTime).replace('T', ' ').slice(0, 19)
      : undefined,
    validFrom: validAt,
    validTo: validAt,
    detailHtml,
    summary: includeContent ? buildSummary(detailHtml) : '',
    status: statusFromApi(rec.status),
    updatedAt: formatUpdatedAt(rec) || new Date().toISOString().slice(0, 10),
    sourceRiskTypes: String(rec.sourceRiskType ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter((item) => /^0[1-9]$/.test(item)),
    sourceOnTap: rec.sourceOnTap === '1' ? '1' : '0',
    dataSource: warningDataSourceLabel(rec.sourceUuid),
    sourceKind: sourceKindFromApi(rec.sourceKind),
    contentOrigin: contentOriginFromApi(rec.contentOrigin, rec.sourceArticleSource),
    sourceUuid: String(rec.sourceUuid ?? '').trim() || undefined,
    sourceFileUrl: String(rec.sourceFileUrl ?? '').trim() || undefined,
    sourceInvoleUs: String(rec.sourceInvoleUs ?? '').trim() || undefined,
    sourceArticleSource: String(rec.sourceArticleSource ?? '').trim() || undefined,
  }
}

/**
 * GET /biz/overseas/warning/detail/{id}
 */
export async function fetchWarningReleaseDetail(id: string): Promise<WarningReleaseRecord | null> {
  const sid = id?.trim()
  if (!sid) return null
  const raw = await http.get<WarningApiRecord>(
    `/biz/overseas/warning/detail/${encodeURIComponent(sid)}`,
  )
  if (!raw || typeof raw !== 'object') return null
  return mapWarningApiToRecord(raw, { includeContent: true })
}

/**
 * GET /biz/overseas/warning/page?current=&size=
 */
export async function fetchWarningReleaseList(
  query: WarningReleaseListQuery = {},
): Promise<WarningReleaseListResult> {
  const current = query.page ?? 1
  const size = query.pageSize ?? 10
  const status =
    query.status && query.status !== 'all'
      ? query.status === 'published'
        ? 'PUBLISHED'
        : 'DRAFT'
      : undefined
  const raw = await http.get<MybatisPage<WarningApiRecord>>('/biz/overseas/warning/page', {
    params: {
      current,
      size,
      status,
      title: query.title?.trim() || undefined,
      country: query.country?.trim() || undefined,
      dataSource: query.dataSource,
      sourceKind: query.sourceKind,
      sourceRiskType: query.sourceRiskType?.trim() || undefined,
      startDate: query.startDate?.trim() || undefined,
      endDate: query.endDate?.trim() || undefined,
    } as Record<string, string | number | undefined>,
  })
  const records = Array.isArray(raw?.records) ? raw.records : []
  const list = records
    .map((row) => mapWarningApiToRecord(row, { includeContent: false }))
    .filter((x): x is WarningReleaseRecord => x != null)
  const total = typeof raw?.total === 'number' ? raw.total : list.length
  return { list, total }
}

/**
 * POST /biz/overseas/warning/publish（以 DRAFT 保存）
 */
export async function createWarningRelease(
  payload: WarningReleasePayload,
): Promise<WarningReleaseRecord> {
  const id = await http.post<string>(
    '/biz/overseas/warning/publish',
    buildWarningPublishDto(payload),
  )
  // 后端 publish 返回 id；这里直接组装一份用于本次 UI 刷新前的回显
  const regionDisplay = formatWarningTargetRegionsDisplay(payload.targetGeonameids)
  return {
    id: String(id),
    title: payload.title,
    countryCode: '',
    countryName: regionDisplay,
    targetCountryCodes: [],
    targetCountryNames: [],
    targetGeonameids: payload.targetGeonameids,
    regionDisplay,
    validFrom: payload.validFrom,
    validTo: payload.validTo,
    detailHtml: payload.detailHtml,
    summary: buildSummary(payload.detailHtml),
    status: 'draft',
    updatedAt: new Date().toISOString().slice(0, 10),
    sourceRiskTypes: payload.sourceRiskTypes ?? [],
    sourceOnTap: payload.sourceOnTap === '1' ? '1' : '0',
    dataSource: '平台',
    sourceKind: payload.sourceKind,
    contentOrigin: payload.contentOrigin,
  }
}

/**
 * POST /biz/overseas/warning/publish（以 DRAFT 保存，携带 id）
 */
export async function updateWarningRelease(
  id: string,
  payload: WarningReleasePayload,
): Promise<WarningReleaseRecord> {
  const sid = id?.trim()
  if (!sid) throw new Error('id 不能为空')
  await http.post<string>(
    '/biz/overseas/warning/publish',
    buildWarningPublishDto(payload, { id: sid }),
  )
  const regionDisplay = formatWarningTargetRegionsDisplay(payload.targetGeonameids)
  return {
    id: sid,
    title: payload.title,
    countryCode: '',
    countryName: regionDisplay,
    targetCountryCodes: [],
    targetCountryNames: [],
    targetGeonameids: payload.targetGeonameids,
    regionDisplay,
    validFrom: payload.validFrom,
    validTo: payload.validTo,
    detailHtml: payload.detailHtml,
    summary: buildSummary(payload.detailHtml),
    status: 'draft',
    updatedAt: new Date().toISOString().slice(0, 10),
    sourceRiskTypes: payload.sourceRiskTypes ?? [],
    sourceOnTap: payload.sourceOnTap === '1' ? '1' : '0',
    dataSource: '平台',
    sourceKind: payload.sourceKind,
    contentOrigin: payload.contentOrigin,
  }
}

/**
 * POST /biz/overseas/warning/delete
 */
export async function deleteWarningRelease(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/warning/delete', { id: sid })
}

/**
 * POST /biz/overseas/warning/publish-by-id/{id}
 * 列表上架只改状态，沿用库里已保存的国家地区，避免行数据缺 geonameid 把地区覆盖成空。
 */
export async function publishWarningRelease(row: WarningReleaseRecord): Promise<void>
export async function publishWarningRelease(id: string): Promise<void>
export async function publishWarningRelease(input: WarningReleaseRecord | string): Promise<void> {
  const sid = typeof input === 'string' ? input.trim() : String(input?.id ?? '').trim()
  if (!sid) throw new Error('id 不能为空')
  await http.post<void>(`/biz/overseas/warning/publish-by-id/${sid}`)
}

/**
 * POST /biz/overseas/warning/push-sms/:id
 */
export async function pushWarningReleaseSms(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>(`/biz/overseas/warning/push-sms/${sid}`)
}

/**
 * POST /biz/overseas/warning/offline
 */
/** 平台改置顶。已发布也可操作，并锁定为平台值。 */
export async function updateWarningOnTap(id: string, sourceOnTap: '0' | '1'): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/warning/on-tap', { id: sid, sourceOnTap })
}

export async function offlineWarningRelease(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/warning/offline', { id: sid })
}

/**
 * POST /biz/overseas/warning/push-sms-batch
 */
export async function pushWarningReleaseSmsBatch(
  warningId: string,
  personnelIds: string[],
): Promise<number> {
  const sid = warningId?.trim()
  if (!sid) throw new Error('warningId 不能为空')
  const ids = Array.isArray(personnelIds)
    ? personnelIds.map((x) => String(x).trim()).filter(Boolean)
    : []
  const added = await http.post<number>('/biz/overseas/warning/push-sms-batch', {
    warningId: sid,
    personnelIds: ids,
  })
  return typeof added === 'number' ? added : 0
}

/**
 * POST /biz/overseas/warning/push-sms-batch-by-query
 * 按当前筛选条件推送全部（后端会筛掉已归国人员）
 */
export async function pushWarningReleaseSmsBatchByQuery(
  query: Omit<WarningPushPersonnelPageQuery, 'page' | 'pageSize'>,
): Promise<number> {
  const sid = query.warningId?.trim()
  if (!sid) throw new Error('warningId 不能为空')
  const added = await http.post<number>('/biz/overseas/warning/push-sms-batch-by-query', {
    warningId: sid,
    searchKey: query.searchKey?.trim() || undefined,
    status: query.status && query.status !== 'ALL' ? query.status : undefined,
    enterpriseId: query.enterpriseId?.trim() || undefined,
    overseasCountry: query.overseasCountry?.trim() || undefined,
    areaCode: query.areaCode?.trim() || undefined,
    pushed: query.pushed || 'ALL',
  })
  return typeof added === 'number' ? added : 0
}

export interface WarningPushSmsPreviewResult {
  total: number
  canPush: number
  cannotPush: number
  noPhoneCount: number
  returnedCount: number
}

export async function previewWarningReleaseSmsBatchByQuery(
  query: Omit<WarningPushPersonnelPageQuery, 'page' | 'pageSize'>,
): Promise<WarningPushSmsPreviewResult> {
  const sid = query.warningId?.trim()
  if (!sid) throw new Error('warningId 不能为空')
  const raw = await http.post<WarningPushSmsPreviewResult>(
    '/biz/overseas/warning/push-sms-batch-by-query/preview',
    {
      warningId: sid,
      searchKey: query.searchKey?.trim() || undefined,
      status: query.status && query.status !== 'ALL' ? query.status : undefined,
      enterpriseId: query.enterpriseId?.trim() || undefined,
      overseasCountry: query.overseasCountry?.trim() || undefined,
      areaCode: query.areaCode?.trim() || undefined,
      pushed: query.pushed || 'ALL',
    },
  )
  return {
    total: typeof raw?.total === 'number' ? raw.total : 0,
    canPush: typeof raw?.canPush === 'number' ? raw.canPush : 0,
    cannotPush: typeof raw?.cannotPush === 'number' ? raw.cannotPush : 0,
    noPhoneCount: typeof raw?.noPhoneCount === 'number' ? raw.noPhoneCount : 0,
    returnedCount: typeof raw?.returnedCount === 'number' ? raw.returnedCount : 0,
  }
}

export type PersonnelBizStatus = 'OVERSEAS' | 'RETURNED'
export type WarningPushedFilter = 'ALL' | 'YES' | 'NO'

export interface WarningPushPersonnelRow {
  id: string
  name: string
  idCard?: string
  /** 列表不再回传手机号；是否可推送看 hasPhone */
  phone?: string
  /** 服务端判定是否有可用明文手机号，供勾选/推送；勿用 phone 反推 */
  hasPhone?: boolean
  domesticProvince: string
  domesticAddressCode: string
  overseasCountry: string
  enterpriseId: string
  status: PersonnelBizStatus
  pushed: boolean
  pushTimes: number
  lastPushTime?: string
  lastPushStatus?: string
}

export interface WarningPushHistoryRow {
  id: string
  warningId: string
  personnelId: string
  personnelName: string
  /** 历史列表不再回传；敏感信息走人员详情 */
  idCard?: string
  phone?: string
  pushUserName?: string
  pushTime?: string
  pushStatus?: string
}

export interface WarningPushPersonnelPageQuery {
  warningId: string
  page?: number
  pageSize?: number
  searchKey?: string
  status?: PersonnelBizStatus | 'ALL'
  enterpriseId?: string
  overseasCountry?: string
  areaCode?: string
  pushed?: WarningPushedFilter
}

export interface WarningPushHistoryPageQuery {
  warningId: string
  page?: number
  pageSize?: number
  personnelId?: string
}

export interface WarningPushPageResult<T> {
  list: T[]
  total: number
}

export async function fetchWarningPushPersonnelPage(
  query: WarningPushPersonnelPageQuery,
): Promise<WarningPushPageResult<WarningPushPersonnelRow>> {
  const current = query.page ?? 1
  const size = query.pageSize ?? 10
  const raw = await http.get<MybatisPage<WarningPushPersonnelRow>>(
    '/biz/overseas/warning/push-personnel-page',
    {
      params: {
        warningId: query.warningId,
        current,
        size,
        searchKey: query.searchKey?.trim() || undefined,
        status: query.status && query.status !== 'ALL' ? query.status : undefined,
        enterpriseId: query.enterpriseId?.trim() || undefined,
        overseasCountry: query.overseasCountry?.trim() || undefined,
        areaCode: query.areaCode?.trim() || undefined,
        pushed: query.pushed || 'ALL',
      } as Record<string, string | number | undefined>,
    },
  )
  const records = Array.isArray(raw?.records) ? raw.records : []
  const total = typeof raw?.total === 'number' ? raw.total : records.length
  return { list: records, total }
}

export async function fetchWarningPushHistoryPage(
  query: WarningPushHistoryPageQuery,
): Promise<WarningPushPageResult<WarningPushHistoryRow>> {
  const current = query.page ?? 1
  const size = query.pageSize ?? 10
  const raw = await http.get<MybatisPage<WarningPushHistoryRow>>(
    '/biz/overseas/warning/push-history-page',
    {
      params: {
        warningId: query.warningId,
        current,
        size,
        personnelId: query.personnelId?.trim() || undefined,
      } as Record<string, string | number | undefined>,
    },
  )
  const records = Array.isArray(raw?.records) ? raw.records : []
  const total = typeof raw?.total === 'number' ? raw.total : records.length
  return { list: records, total }
}
